import { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import useThemeColor from "../../hooks/useThemeColor";
import { Panel, PanelHeader, PanelBody } from "./../../components/panel/panel.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import schedule from "../../components/CustomComponents/TimeSchedule/schedule.js";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import CommonTimeSlotForAllWeek from "../../components/CustomComponents/TimeSchedule/commonTimeSlotForAllWeek.js";
import ScheduleManager from "../../components/CustomComponents/TimeSchedule/weeklySlots.js";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";

function AddMenu() {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [enable, setEnable] = useState(true);
  const [regularHours, setRegularHours] = useState(true);
  const [menuName, setMenuName] = useState("");
  const [epos, setEpos] = useState(true);
  const [kiosk, setKiosk] = useState(false);
  const [online, setOnline] = useState(true);
  const [delivery, setDelivery] = useState(true);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [menuGroups, setMenuGroups] = useState([
    {
      menuGroupID: 0,
      menu_ID: 0,
      localizeID: "en",
      compID: 0,
      menuGroup_Name: "",
      menuGroup_Publish: true,
      menuGroup_Orderby: 0,
      updateDateTime: new Date().toISOString(),
    },
  ]);

  const [useSameTimeSlots, setUseSameTimeSlots] = useState(true);
  const [commonTimeSlot, setCommonTimeSlot] = useState([{ from: "00:00", to: "23:59" }]);
  const [schedules, setSchedules] = useState(schedule);

  const initialFormSnapshotRef = useRef(null);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      menuName: menuName || "",
      enable: Boolean(enable),
      regularHours: Boolean(regularHours),
      epos: Boolean(epos),
      kiosk: Boolean(kiosk),
      online: Boolean(online),
      delivery: Boolean(delivery),
      useSameTimeSlots: Boolean(useSameTimeSlots),
      commonTimeSlot,
      schedules,
      menuGroups: (menuGroups || []).map((g) => ({
        menuGroupID: g?.menuGroupID || 0,
        menuGroup_Name: g?.menuGroup_Name || "",
        menuGroup_Publish: Boolean(g?.menuGroup_Publish),
        menuGroup_Orderby: g?.menuGroup_Orderby ?? 0,
      })),
    });
  }, [
    menuName,
    enable,
    regularHours,
    epos,
    kiosk,
    online,
    delivery,
    useSameTimeSlots,
    commonTimeSlot,
    schedules,
    menuGroups,
  ]);

  useEffect(() => {
    if (initialFormSnapshotRef.current === null) {
      initialFormSnapshotRef.current = getFormSnapshot();
    }
  }, [getFormSnapshot]);

  const isDirty =
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  const handleMenuGroupChange = (index, value) => {
    setMenuGroups((prev) =>
      prev.map((group, i) => (i === index ? { ...group, menuGroup_Name: value } : group))
    );
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;
    const newMenuGroups = [...menuGroups];
    const [draggedItem] = newMenuGroups.splice(draggedIndex, 1);
    newMenuGroups.splice(dropIndex, 0, draggedItem);
    setMenuGroups(
      newMenuGroups.map((group, idx) => ({ ...group, menuGroup_Orderby: idx }))
    );
    setDraggedIndex(null);
  };
  const handleDragEnd = () => setDraggedIndex(null);

  const handleAddMenuGroup = () => {
    setMenuGroups((prev) => [
      ...prev,
      {
        menuGroupID: 0,
        menu_ID: 0,
        localizeID: "en",
        compID: CompID,
        menuGroup_Name: "",
        menuGroup_Publish: true,
        menuGroup_Orderby: prev.length,
        updateDateTime: new Date().toISOString(),
      },
    ]);
  };

  const handleDeleteMenuGroup = (index) => {
    setMenuGroups((prev) => prev.filter((_, i) => i !== index));
  };

  const applyCommonTimeSlotToAll = () => {
    setSchedules((prev) =>
      prev.map((s) => ({ ...s, timeSlots: commonTimeSlot.map((slot) => ({ ...slot })) }))
    );
  };

  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;

    if (!menuName.trim()) {
      showErrorToast("Please enter a menu name.");
      return false;
    }
    try {
      setLoading(true);

      const formattedSchedules = [];
      if (!regularHours) {
        if (useSameTimeSlots) {
          const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
          days.forEach((day) => {
            if (commonTimeSlot.length > 0) {
              commonTimeSlot.forEach((slot) => {
                formattedSchedules.push({
                  ID: 0,
                  CompID: CompID,
                  menu_ID: 0,
                  DayName: day,
                  OpenTime: moment(slot.from, ["h:mm A"]).format("hh:mm A"),
                  CloseTime: moment(slot.to, ["h:mm A"]).format("hh:mm A"),
                  UpdateDateTime: new Date().toISOString(),
                });
              });
            }
          });
        } else {
          schedules.forEach((schedule) => {
            if (schedule.checked && schedule.timeSlots.length > 0) {
              schedule.timeSlots.forEach((slot) => {
                if (moment(slot.from, ["h:mm A"]).isValid() && moment(slot.to, ["h:mm A"]).isValid()) {
                  formattedSchedules.push({
                    ID: 0,
                    CompID: CompID,
                    menu_ID: 0,
                    DayName: schedule.day,
                    OpenTime: moment(slot.from, ["h:mm A"]).format("hh:mm A"),
                    CloseTime: moment(slot.to, ["h:mm A"]).format("hh:mm A"),
                    UpdateDateTime: new Date().toISOString(),
                  });
                }
              });
            }
          });
        }
      }

      const menuPayload = {
        menu_ID: 0,
        compID: CompID,
        menu_Name: menuName,
        menu_Publish: enable,
        menu_Orderby: 0,
        isActiveforTill: epos,
        isActiveforKIOSK: kiosk,
        isActiveforTakeAway: online,
        isActiveforDelivery: delivery,
        isAvailableInTimeIntervals: !regularHours,
        updateDateTime: new Date().toISOString(),
        menuWeeklyTimings: formattedSchedules,
        products: [],
      };

      const menuResponse = await axiosInstance.post(APIs.POST_ADD_MENU, menuPayload);
      if (!menuResponse.data.status) {
        showErrorToast(menuResponse.data.message || "Failed to add Menu");
        return false;
      }
      const menuID = menuResponse.data.data.menu_ID;

      const updatedMenuGroups = menuGroups
        .filter((group) => group.menuGroup_Name?.trim().length > 0)
        .map((gro) => ({ ...gro, menu_ID: menuID, compID: CompID }));

      if (updatedMenuGroups.length > 0) {
        const menuGroupResponse = await axiosInstance.post(
          APIs.POST_ADD_MENU_GROUP_LIST,
          updatedMenuGroups
        );
        if (!menuGroupResponse.data.status) {
          showErrorToast(menuGroupResponse.data.message || "Failed to add Menu Groups");
          return false;
        }
      }
      showSuccessToast("Menu added successfully");
      initialFormSnapshotRef.current = getFormSnapshot();
      if (exitAfterSave) {
        navigate("/menu/manageMenu");
      }
      return true;
    } catch (error) {
      showErrorToast(
        error.response?.data?.message || "An error occurred while adding the Menu"
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  return (
    <div>
      
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/menu/manageMenu">Manage Menu</Link>
            </li>
            <li className="breadcrumb-item active">Add Menu</li>
          </ol>
          <h1 className="page-header mb-0">Add Menu</h1>
        </div>
        <SaveActions
          onCancel={() => navigate("/menu/manageMenu")}
          onSave={() => handleSubmit({ exitAfterSave: false })}
          onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
          saving={loading}
        />
      </div>

      <GridSectionLoader loading={loading} minHeight="280px">
<div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>General</PanelHeader>
          <PanelBody>
            <div
              className="p-4 rounded-3 shadow-sm"
              style={{
                backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                border: isDarkMode ? "1px solid #404040" : undefined,
                color: isDarkMode ? "#ffffff" : undefined,
              }}
            >
              <div className="row mb-4 align-items-center">
                <div className="col-md-6">
                  <FormInput
                    label="Menu Name *"
                    type="text"
                    value={menuName}
                    onchange={(e) => setMenuName(e.target.value)}
                    placeHolder="Enter menu name"
                    name="menuName"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <div className="row row-cols-2 g-3">
                    {[
                      { label: "Enable Menu", checked: enable, toggle: () => setEnable(!enable) },
                      { label: "KIOSK", checked: kiosk, toggle: () => setKiosk(!kiosk) },
                      { label: "Online Orders", checked: online, toggle: () => setOnline(!online) },
                      { label: "Delivery", checked: delivery, toggle: () => setDelivery(!delivery) },
                      { label: "E-POS", checked: epos, toggle: () => setEpos(!epos) },
                    ].map(({ label, checked, toggle }) => (
                      <div key={label} className="col">
                        <ToggleSwitch
                          isChecked={checked}
                          onToggle={toggle}
                          label={label}
                          themeColor={themeColor}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="row g-4 mt-2">
                <div className="col-12">
                  <div
                    className="card p-3"
                    style={{
                      backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                      border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <h5 className="card-title mb-3">Time Scheduling</h5>
                    <div className="row g-3 d-flex align-items-center p-4">
                      <div className="col-6">
                        <ToggleSwitch
                          isChecked={regularHours}
                          onToggle={() => setRegularHours(!regularHours)}
                          label="Available in regular business hours"
                          themeColor={themeColor}
                        />
                      </div>
                    </div>
                    {!regularHours && (
                      <>
                        <hr />
                        <div className="row g-3 d-flex align-items-center p-4">
                          <div className="col-6">
                            <ToggleSwitch
                              isChecked={useSameTimeSlots}
                              onToggle={() => {
                                setUseSameTimeSlots(!useSameTimeSlots);
                                if (!useSameTimeSlots) applyCommonTimeSlotToAll();
                              }}
                              label="Use same time slots for all days"
                              themeColor={themeColor}
                            />
                          </div>
                        </div>
                        <hr />
                        {useSameTimeSlots ? (
                          <CommonTimeSlotForAllWeek
                            setCommonTimeSlots={setCommonTimeSlot}
                            commonTimeSlots={commonTimeSlot}
                            isProfilePage={false}
                          />
                        ) : (
                          <ScheduleManager
                            schedules={schedules}
                            setSchedules={setSchedules}
                            isProfilePage={false}
                            isDarkMode={isDarkMode}
                            themeColor={themeColor}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="row g-4 mt-2">
                <div className="col-12">
                  <div
                    className="card p-3"
                    style={{
                      backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                      border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <h5 className="card-title mb-1">Menu Groups</h5>
                    <p
                      className="small mb-3"
                      style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}
                    >
                      Add groups and drag to reorder.
                    </p>
                    {menuGroups.map((group, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center mb-2 rounded-3 p-2"
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        style={{
                          opacity: draggedIndex === index ? 0.7 : 1,
                          cursor: "grab",
                          backgroundColor: isDarkMode ? "#1e2428" : "#f8f9fa",
                          border: isDarkMode
                            ? "1px solid #404040"
                            : "1px solid #e9ecef",
                        }}
                      >
                        <i
                          className="fas fa-grip-vertical me-2"
                          style={{
                            color: isDarkMode ? "#adb5bd" : "#6c757d",
                          }}
                        />
                        <div className="flex-grow-1">
                          <FormInput
                            placeHolder={`Group ${index + 1} (e.g., Appetizers, Main Course)`}
                            type="text"
                            value={group.menuGroup_Name}
                            onchange={(e) =>
                              handleMenuGroupChange(index, e.target.value)
                            }
                          />
                        </div>
                        {menuGroups.length > 1 && (
                          <i
                            className="fas fa-trash text-danger ms-2"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleDeleteMenuGroup(index)}
                          />
                        )}
                      </div>
                    ))}
                    <div className="mt-3">
                      <button
                        type="button"
                        className="btn"
                        onClick={handleAddMenuGroup}
                        style={{
                          backgroundColor: isDarkMode
                            ? `${themeColor}18`
                            : `${themeColor}12`,
                          border: `1px solid ${themeColor}`,
                          color: themeColor,
                          borderRadius: 8,
                          fontWeight: 600,
                        }}
                      >
                        <i className="fas fa-plus me-2"></i>Add Group
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>
</GridSectionLoader>
    </div>
  );
}

export default AddMenu;
